import {
  ActionApiModel,
  ActionOptionApiModel,
  ConditionApiModel,
  ImportedItemSourceApiModel,
  PackageApiModel,
  RuleApiModel,
  ServiceApiModel,
  SetupApiModel
} from './types';
import { useAuth } from 'react-oidc-context';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { UnauthorisedResponse } from '~/lib/api/unauthorised-response';
import { getApiPath } from './get-api-path';
import { ApiResponse } from '~/lib/api/api-response';
import { parseResult } from '~/lib/api/parse-result';
import { authedFetch } from '~/lib/api/authed-fetch';
import { DefaultRuleManagerQueue } from './cache';
import { getOrAdd } from '~/apps/smartsend/lib/helpers/ApiCache';
import { useCallback } from 'react';

export interface IApiResponse<T> {
  success: boolean;
  result: T | null;
}

export function useRuleManagerApi() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const getServices = useCallback(async (): Promise<ApiResponse<ServiceApiModel[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/service/list`);
    return getOrAdd<ServiceApiModel[]>(DefaultRuleManagerQueue, 'MEMORY', `rulemanager-services`, () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user?.access_token, tenant]);

  const getSourceServices = useCallback(
    async (source: string): Promise<ApiResponse<ServiceApiModel[]>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/service/external/list/${source}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<ServiceApiModel[]>(response);
    },
    [user?.access_token, tenant]
  );

  const getMarketplaceServices = useCallback(async (): Promise<ApiResponse<ServiceApiModel[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/service/marketplace/list`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<ServiceApiModel[]>(response);
  }, [user?.access_token, tenant]);

  const getConditions = useCallback(async (): Promise<ApiResponse<ConditionApiModel[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/condition/list`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<ConditionApiModel[]>(response);
  }, [user?.access_token, tenant]);

  const getActions = useCallback(async (): Promise<ApiResponse<ActionApiModel[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/action/list`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<ActionApiModel[]>(response);
  }, [user?.access_token, tenant]);

  const getActionOptions = useCallback(
    async (action: string): Promise<ApiResponse<ActionOptionApiModel[]>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/action/${encodeURIComponent(action)}/options`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<ActionOptionApiModel[]>(response);
    },
    [user?.access_token, tenant]
  );

  const getBulkActionOptions = useCallback(
    async (actions: string[]): Promise<ApiResponse<{ [key: string]: ActionOptionApiModel[] }>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/action/bulk/${actions.map(encodeURIComponent).join(',')}/options`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<{ [key: string]: ActionOptionApiModel[] }>(response);
    },
    [user?.access_token, tenant]
  );

  const getSetup = useCallback(async (): Promise<ApiResponse<SetupApiModel>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/setup`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<SetupApiModel>(response);
  }, [user?.access_token, tenant]);

  const getSources = useCallback(async (): Promise<ApiResponse<ImportedItemSourceApiModel[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/importeditemsource/list`);
    return getOrAdd<ImportedItemSourceApiModel[]>(DefaultRuleManagerQueue, 'MEMORY', `rulemanager-sources`, () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user?.access_token, tenant]);

  const getpackages = useCallback(async (): Promise<ApiResponse<PackageApiModel[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/package/list`);
    return getOrAdd<PackageApiModel[]>(DefaultRuleManagerQueue, 'MEMORY', `rulemanager-packages`, () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user?.access_token, tenant]);

  const getRules = useCallback(async (): Promise<ApiResponse<RuleApiModel[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(tenant, `/rule/list`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<RuleApiModel[]>(response);
  }, [user?.access_token, tenant]);

  const createRule = useCallback(
    async (model: RuleApiModel): Promise<ApiResponse<{ id: number }>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/rule/create`);
      const requestOptions = {
        method: 'POST',
        body: JSON.stringify(model)
      };
      const response = await authedFetch(tenant, url, requestOptions, user?.access_token);
      return parseResult<{ id: number }>(response);
    },
    [user?.access_token, tenant]
  );

  const updateRule = useCallback(
    async (model: RuleApiModel): Promise<ApiResponse<null>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/rule/update/${model.id}`);
      const requestOptions = {
        method: 'PUT',
        body: JSON.stringify(model)
      };
      const response = await authedFetch(tenant, url, requestOptions, user?.access_token);
      return await parseResult<null>(response);
    },
    [user?.access_token, tenant]
  );

  const updatePriority = useCallback(
    async (model: number[]): Promise<ApiResponse<null>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/rule/setpriorityorder`);
      const requestOptions = {
        method: 'POST',
        body: JSON.stringify(model)
      };
      const response = await authedFetch(tenant, url, requestOptions, user?.access_token);
      return await parseResult<null>(response);
    },
    [user?.access_token, tenant]
  );

  const deleteRule = useCallback(
    async (id: number): Promise<ApiResponse<null>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(tenant, `/rule/delete`);
      const requestOptions = {
        method: 'POST',
        body: JSON.stringify({ id })
      };
      const response = await authedFetch(tenant, url, requestOptions, user?.access_token);
      return await parseResult<null>(response);
    },
    [user?.access_token, tenant]
  );

  return {
    getServices,
    getSourceServices,
    getMarketplaceServices,
    getConditions,
    getActions,
    getActionOptions,
    getBulkActionOptions,
    getSetup,
    getSources,
    getpackages,
    getRules,
    createRule,
    updateRule,
    updatePriority,
    deleteRule
  } as const;
}
